'use client';

import * as React from 'react';
import { useFormContext } from 'react-hook-form';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';

interface FormMaskedInputProps {
  name: string;
  label: string;
  mask: string;
  placeholder?: string;
  hint?: string;
  optional?: boolean;
  disabled?: boolean;
}

/**
 * Format a value according to a mask pattern
 * Mask characters:
 * - 0 or 9: digit (0-9)
 * - Other characters are literal separators
 */
function formatWithMask(value: string, mask: string): string {
  // Strip all non-digits from input
  const digits = value.replace(/\D/g, '');

  let result = '';
  let digitIndex = 0;

  for (let i = 0; i < mask.length && digitIndex < digits.length; i++) {
    const maskChar = mask[i];
    if (maskChar === '0' || maskChar === '9') {
      result += digits[digitIndex];
      digitIndex++;
    } else {
      result += maskChar;
    }
  }

  return result;
}

/**
 * Masked input component for formatted fields like phone, zip, tax ID
 *
 * Common masks:
 * - Phone: "000-000-0000"
 * - Tax ID (EIN): "00-0000000"
 * - ZIP Code: "00000"
 *
 * Mask characters:
 * - 0: digit (0-9)
 */
export function FormMaskedInput({
  name,
  label,
  mask,
  placeholder,
  hint,
  optional,
  disabled,
}: FormMaskedInputProps) {
  const form = useFormContext();

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    fieldOnChange: (value: string) => void
  ) => {
    const formatted = formatWithMask(e.target.value, mask);
    fieldOnChange(formatted);
  };

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>
            {label}
            {optional && <span className="ml-1 text-muted-foreground">(optional)</span>}
          </FormLabel>
          <FormControl>
            <Input
              type="tel"
              placeholder={placeholder}
              disabled={disabled}
              value={field.value || ''}
              onChange={(e) => handleChange(e, field.onChange)}
              onBlur={field.onBlur}
              ref={field.ref}
            />
          </FormControl>
          {hint && <FormDescription>{hint}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
